import React from "react";
import { Alert, Modal } from "antd";
import useLang from "../../../hooks/useLang";

interface DriverAssignInfoModalProps {
	open: boolean;
	/** Shown so the admin reads the actual name they are about to write, not a placeholder. */
	userName: string;
	orderCount: number;
	onClose: () => void;
}

/**
 * Explains what ticking "also change the delivery driver" does before the admin commits to it.
 * Reading-only — it has no OK/Cancel semantics, closing it changes nothing.
 */
const DriverAssignInfoModal = ({
	open,
	userName,
	orderCount,
	onClose,
}: DriverAssignInfoModalProps) => {
	const { strings } = useLang("bulkOrderOperations");
	const copy = strings?.statusUpdates;

	const paragraphs = [
		copy?.assignDriverInfo1 ??
			"DeliveryDriverId is the driver credited with delivering the order. It is what driver settlement pays against, and what the AddBalance operation reads.",
		copy?.assignDriverInfo2 ??
			"Leave this OFF and the orders keep the driver they already have. Only the status changes, which is what the manual scripts do.",
		copy?.assignDriverInfo3 ??
			"Turn it ON and the responsible user you selected replaces the current driver on every order that gets updated. Use it when the wrong driver is part of what you are fixing.",
		copy?.assignDriverInfo4 ??
			"The driver's price is never changed, and orders already at the target status are skipped — so they keep their existing driver too.",
		copy?.assignDriverInfo5 ??
			"The previous driver is not recorded anywhere. This cannot be undone from this page.",
	];

	return (
		<Modal
			open={open}
			title={
				copy?.assignDriverInfoTitle ?? "Changing the delivery driver"
			}
			onCancel={onClose}
			onOk={onClose}
			okText={copy?.assignDriverInfoClose ?? "Got it"}
			cancelButtonProps={{ style: { display: "none" } }}
		>
			<div className="flex flex-col gap-3 py-2">
				{paragraphs.map((text) => (
					<p key={text} className="m-0 text-sm">
						{text}
					</p>
				))}
				{/* The concrete consequence, spelled out with the actual name and count. */}
				<Alert
					type="warning"
					showIcon
					message={
						copy?.assignDriverInfoSummary ??
						"If you turn this on and confirm:"
					}
					description={`${
						userName ||
						copy?.assignDriverInfoNoUser ||
						"the user you select"
					} → ${orderCount}`}
				/>
			</div>
		</Modal>
	);
};

export default DriverAssignInfoModal;
